import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Recurrence : famille "plan récurrent".
 *
 * Un PLAN dit « crée cette fiche-là, à ce rythme-là » : le service mensuel d'un
 * client le 15 de chaque mois, l'entretien d'une chaudière six mois après le
 * précédent. Le plan vit au socle, pas dans une app : il vaut pour TOUTES les
 * familles, comme un modèle de fiche (`template.ts`).
 *
 * LE PLAN NE PORTE PAS DE CONTENU, il porte un RYTHME et un MODÈLE
 * (`templateId`). Ce que chaque fiche reçoit (ses champs, ses fiches liées)
 * vit dans le modèle cité, une seule fois : corriger le modèle corrige toutes
 * les occurrences À VENIR, et le serveur relit le modèle à chaque passage. Un
 * contenu recopié dans le plan serait un fait écrit deux fois, qui divergerait
 * du modèle au premier changement. Le modèle tient en retour la liste des plans
 * qui le citent (`plansIds`, `template.ts`) : c'est ce qui l'empêche d'être
 * retiré sous un plan vivant.
 *
 * CE N'EST PAS LE CLIENT QUI CRÉE LES FICHES. Un passage quotidien côté serveur
 * (`functions/src/recurrence/`, docs/FUNCTIONS.md) lit les plans actifs, calcule
 * les occurrences dues et crée les fiches. Le navigateur ne calcule JAMAIS une
 * occurrence : il écrit un plan, et il le lit. D'où les deux moitiés de ce
 * schéma, et c'est la chose à savoir avant d'y toucher :
 *   - les champs du PLAN (`recurrenceCoreFields`), écrits par l'utilisateur ;
 *   - les champs MACHINE (`recurrenceMachineFields`), écrits par le seul
 *     serveur. Les Security Rules REFUSENT toute écriture client qui les touche
 *     (`RECURRENCE_CLEFS_MACHINE`, firebase/firestore.rules) : un client qui
 *     reculerait le curseur ferait recréer des fiches déjà créées.
 *
 * CE QUE `type` PORTE ICI, même bizarrerie que les modèles : la FAMILLE CIBLE
 * (`projects`), pas un sous-type métier. C'est ce qui laisse une porte filtrer
 * côté serveur (`jeu: { family: 'recurrences', type: 'projects' }`) sans rien
 * ajouter au socle de lecture. Le sous-type des fiches créées est `cibleType`.
 *
 * TOUT EST NATIF ET À PLAT, aucun champ dans `customFields` : l'écran générique
 * édite un champ natif sans déclaration de champ, et le serveur (qui ne peut
 * importer aucun `@taki/*`) lit un document dont la forme est fixe.
 *
 * DES MÉCANISMES NOMMÉS, JAMAIS UNE RRULE : `rythmeUnite` + `rythmeIntervalle`
 * + `joursSemaine` / `jourDuMois`. Une RRULE sait tout dire, donc ne se relit
 * pas ; ces cinq champs se lisent dans une fiche par quelqu'un qui ne code pas.
 *
 * LES LISTES FERMÉES CI-DESSOUS SONT ÉCRITES DEUX FOIS, et c'est assumé :
 * `functions/` ne peut dépendre d'aucun `@taki/*` (docs/FUNCTIONS.md), donc
 * `functions/src/recurrence/contrat.ts` les recopie, et un test du SDK compare
 * les deux fichiers. Chacune tient sur UNE ligne pour que ce test la lise.
 */

/** Les trois états d'un plan. `termine` est posé par le serveur quand `fin` est passée. */
export const RECURRENCE_STATUTS = ["actif", "suspendu", "termine"] as const;

/** Les deux déclencheurs : au calendrier, ou un délai après la réalisation de la fiche précédente. */
export const RECURRENCE_MODES = ["calendrier", "apresRealisation"] as const;

/** L'unité du rythme calendaire. `trimestre` vaut trois mois, ancrés sur le mois de `debut`. */
export const RECURRENCE_UNITES_RYTHME = ["jour", "semaine", "mois", "trimestre", "an"] as const;

/** Les jours de la semaine, lundi d'abord (semaine ISO). */
export const RECURRENCE_JOURS_SEMAINE = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"] as const;

/** L'unité du délai après réalisation. */
export const RECURRENCE_UNITES_DELAI = ["jours", "mois"] as const;

/** Le verdict du dernier passage. La chaîne vide dit « jamais passé ». */
export const RECURRENCE_RESULTATS = ["ok", "echec", ""] as const;

/**
 * LES CLEFS QUE SEUL LE SERVEUR ÉCRIT, dans l'ordre du schéma. Les Security
 * Rules en recopient la liste (elles n'importent rien), et c'est elle qu'un
 * test de rules touche une à une.
 */
export const RECURRENCE_CLEFS_MACHINE = ["curseur", "prochaineOccurrence", "journal", "nbProduites", "dernierPassageAt", "dernierResultat", "derniereProduiteFamily", "derniereProduiteId", "derniereProduiteOccurrence"] as const;

/** Un état de plan. */
export type RecurrenceStatut = (typeof RECURRENCE_STATUTS)[number];
/** Un déclencheur. */
export type RecurrenceMode = (typeof RECURRENCE_MODES)[number];
/** Une unité de rythme. */
export type RecurrenceUniteRythme = (typeof RECURRENCE_UNITES_RYTHME)[number];
/** Un jour de semaine. */
export type RecurrenceJourSemaine = (typeof RECURRENCE_JOURS_SEMAINE)[number];
/** Une unité de délai. */
export type RecurrenceUniteDelai = (typeof RECURRENCE_UNITES_DELAI)[number];

/**
 * UNE DATE PURE, `AAAA-MM-JJ` : un jour sans heure (AGENTS.md section 7). Un
 * plan raisonne en JOURS, jamais en instants : « le 15 » est le 15 à Paris
 * comme à Tokyo, et un Timestamp à minuit changerait de jour selon le fuseau
 * de qui le lit.
 */
const jourPur = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "une date pure s'écrit AAAA-MM-JJ");

/** Champs du PLAN, écrits par l'utilisateur (hors champs standard du socle). */
export const recurrenceCoreFields = {
  orgId: z.string(),
  /**
   * La FAMILLE CIBLE, clef du repository dans `TakiSdk` ("projects", "tasks").
   * Même mot que le `type` d'un modèle et que `taki.fields.json`.
   */
  type: z.string().min(1),
  /** Le sous-type des fiches créées ("projet", "intervention"). */
  cibleType: z.string().min(1),
  /** Nom du plan, tel qu'il se lit dans la liste des plans. */
  name: z.string().min(1),
  /** Ce à quoi le plan sert, en une phrase. Optionnel. */
  description: z.string().optional(),
  /** Id de la fiche dont le plan a été tiré. Informatif, la fiche peut disparaître. */
  sourceId: z.string().min(1).optional(),
  /**
   * `actif` : le passage quotidien le lit. `suspendu` : il l'ignore (c'est
   * l'état d'un plan qu'on vient de tirer d'une fiche, pour qu'il ne crée rien
   * avant d'être relu). `termine` : `fin` est passée, posé par le serveur.
   */
  status: z.enum(RECURRENCE_STATUTS).default("actif"),

  /* ------------------------------ Le déclencheur ------------------------------ */

  mode: z.enum(RECURRENCE_MODES),
  rythmeUnite: z.enum(RECURRENCE_UNITES_RYTHME),
  /** Tous les N (jours, semaines, mois...). 1 = chaque. */
  rythmeIntervalle: z.number().int().min(1).default(1),
  /**
   * Les jours d'une semaine, pour `rythmeUnite: 'semaine'` seulement. Absent :
   * le jour de semaine de `debut`.
   */
  joursSemaine: z.array(z.enum(RECURRENCE_JOURS_SEMAINE)).optional(),
  /**
   * Le jour du mois, pour `mois`, `trimestre` et `an`. Absent : le jour de
   * `debut`. Un 31 tombe le dernier jour d'un mois plus court, et l'ancrage se
   * fait sur l'INDEX du mois, jamais sur l'occurrence précédente : un 31 janvier
   * passé par le 28 février revient bien au 31 mars (pas de dérive).
   */
  jourDuMois: z.number().int().min(1).max(31).optional(),
  /** Le premier jour possible, date pure. */
  debut: jourPur,
  /** Le dernier jour possible, date pure. Passé, le plan devient `termine`. */
  fin: jourPur.optional(),
  /** Créer la fiche N jours AVANT son occurrence (0 = le jour même). */
  avanceJours: z.number().int().min(0).default(0),
  /**
   * Les occurrences à NE PAS créer (leur clef `AAAA-MM-JJ`) : le service
   * d'août, pendant la fermeture. Écrit par l'utilisateur, lu par le serveur.
   */
  sauts: z.array(z.string()).default([]),

  /* ---------------------- Après réalisation (mode `apresRealisation`) ---------------------- */

  /** CHEMIN DE STOCKAGE du champ qui dit « fait » (`status`, `customFields.etat`). */
  realisationChamp: z.string().optional(),
  /** Les valeurs de ce champ qui valent « fait » (`termine`, `livre`). */
  realisationValeurs: z.array(z.string()).optional(),
  /** CHEMIN du champ qui porte le jour de réalisation. Absent : `updatedAt` de la fiche. */
  realisationDateChamp: z.string().optional(),
  /** Le délai entre la réalisation et la fiche suivante. */
  delaiNombre: z.number().int().min(0).optional(),
  delaiUnite: z.enum(RECURRENCE_UNITES_DELAI).optional(),

  /* ------------------------------- L'action -------------------------------- */

  /**
   * L'id du MODÈLE (`templates/{id}`) dont chaque occurrence est une copie :
   * ses champs, `customFields` compris, et ses fiches liées. REQUIS et
   * IMMUABLE : les Security Rules refusent un plan qui n'en cite pas, et tout
   * changement après coup. Même famille (`type`) et même sous-type
   * (`cibleType` = `targetType` du modèle) que le plan, vérifiés par le SDK.
   * Les dates d'une occurrence ne viennent PAS du modèle : elles se posent par
   * les chemins nommés plus bas.
   */
  templateId: z.string().min(1),
  /** CHEMIN DE STOCKAGE qui reçoit le libellé de l'occurrence (`name`, `customFields.libelle`). */
  champLibelle: z.string().optional(),
  /**
   * Le libellé d'une occurrence, avec quatre jetons FERMÉS : `{mois}`
   * (septembre), `{annee}`, `{jour}`, `{semaine}` (numéro ISO). Vide : le nom
   * du plan.
   */
  modeleLibelle: z.string().optional(),
  /**
   * CHEMIN qui reçoit le jour de l'occurrence. Sous `customFields.` : une date
   * pure ; à la racine : un instant à 12:00:00Z, midi pour qu'aucun fuseau
   * d'Europe ne le fasse changer de jour.
   */
  champDateOccurrence: z.string().optional(),
  /** CHEMIN qui reçoit l'échéance : le jour de l'occurrence plus `delaiEcheanceJours`. */
  champDateEcheance: z.string().optional(),
  delaiEcheanceJours: z.number().int().min(0).optional(),
} as const;

/**
 * Champs MACHINE, écrits par le seul serveur (`updatedBy: 'system:recurrence'`).
 * Ils ont tous une valeur par défaut : un plan que le serveur n'a jamais lu se
 * lit quand même, avec un curseur vide et un journal vide.
 */
export const recurrenceMachineFields = {
  /** La dernière occurrence SERVIE (créée, existante ou sautée), `AAAA-MM-JJ`. Vide : aucune. */
  curseur: z.string().default(""),
  /** La prochaine occurrence que le serveur créera, pour l'affichage. Vide : aucune. */
  prochaineOccurrence: z.string().default(""),
  /** Le journal du plan, le plus récent d'abord, 40 lignes au plus. */
  journal: z.array(z.string()).default([]),
  /** Le nombre de fiches créées par ce plan. */
  nbProduites: z.number().int().min(0).default(0),
  dernierPassageAt: z.date().nullable().default(null),
  dernierResultat: z.enum(RECURRENCE_RESULTATS).default(""),
  /** La dernière fiche créée, que le mode `apresRealisation` relit. */
  derniereProduiteFamily: z.string().optional(),
  derniereProduiteId: z.string().optional(),
  derniereProduiteOccurrence: z.string().optional(),
} as const;

/**
 * Schéma complet de la famille recurrence (champs standard + plan + machine).
 *
 * `templateId` y est REDÉFINI à la LECTURE, avec un défaut vide : un plan
 * d'avant les modèles n'en porte pas (son contenu vivait dans le plan, et ce
 * contenu est désormais ignoré). Le rendre illisible ferait tomber toute la
 * liste des plans de l'org, alors qu'il doit rester visible pour être
 * supprimé. Le serveur le suspend, les rules le laissent supprimer ; la
 * création, elle, exige toujours un modèle.
 */
export const RecurrenceSchema = z.object({
  ...baseEntityFields,
  ...recurrenceCoreFields,
  ...recurrenceMachineFields,
  templateId: z.string().default(""),
});

/** Type d'un plan récurrent. */
export type Recurrence = z.infer<typeof RecurrenceSchema>;

/**
 * Schéma des données fournies par l'appelant à la création. Ni les champs
 * standard (posés par le SDK) ni les champs MACHINE (posés par le serveur,
 * refusés par les rules) n'y sont.
 */
export const RecurrenceCreateInputSchema = z.object({
  ...recurrenceCoreFields,
  ...customFieldsField,
});

/**
 * Type de l'entrée de création. `z.input` et non `z.infer` : les champs à
 * défaut (`status`, `rythmeIntervalle`, `avanceJours`, `sauts`)
 * peuvent être omis par l'appelant, c'est le schéma qui les pose.
 */
export type RecurrenceCreateInput = z.input<typeof RecurrenceCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel). `orgId`, `type` et
 * `templateId` sont immuables (Security Rules), les champs machine
 * n'appartiennent qu'au serveur : aucun n'est accepté ici. Changer de modèle,
 * c'est un autre plan.
 */
export const RecurrenceUpdateInputSchema = z
  .object({ ...recurrenceCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true, templateId: true })
  .partial();

/** Type du patch de mise à jour d'un plan. */
export type RecurrenceUpdateInput = z.infer<typeof RecurrenceUpdateInputSchema>;

/** Les mois en toutes lettres, tels qu'un titre humain les ecrit. */
const MOIS_EN_LETTRES =
  "janvier|fevrier|février|mars|avril|mai|juin|juillet|aout|août|septembre|octobre|novembre|decembre|décembre";

/**
 * LE TITRE D'UNE FICHE SANS SA PERIODE : « Hôtel des Bains, contenu Insta,
 * septembre 2026 » devient « Hôtel des Bains, contenu Insta ».
 *
 * POURQUOI CA COMPTE. « Créer un plan récurrent » part d'une fiche REELLE, celle d'un
 * mois donne. Recopier son titre tel quel ferait naitre chaque mois un service
 * « septembre 2026 », et un nom de plan qui date du premier mois. On retire donc
 * la periode EN FIN de titre (un mois en lettres suivi d'une annee, ou une
 * annee seule), et seulement la : un mois au milieu d'un titre (« Salon de mai,
 * stand ») est du vocabulaire, pas une periode.
 *
 * Fonction PURE, partagee par l'ecran (le nom propose) et le SDK (le gabarit
 * du libelle), pour que les deux coupent au meme endroit.
 */
export function titreSansPeriode(titre: string): string {
  const periode = new RegExp(`[\\s,;:/-]*(?:(?:${MOIS_EN_LETTRES})\\s+)?(?:19|20)\\d{2}\\s*$`, "iu");
  const coupe = titre.replace(periode, "").trim();
  return coupe === "" ? titre.trim() : coupe;
}
